import React, { useEffect, useRef, useState } from 'react';
import { PersonAccountGroup } from '../../services/investor.service';
import { ServiceProviderDto } from '../../services/profile.service';

/**
 * Dashboard-style gradient banner with the two account filters — Service Provider (white
 * pill) × Accounts (gold pill, person-based) — both default All. Data-driven: the host page
 * fetches persons/SPs and owns the selection state; the banner only renders + calls back.
 * Matching rule for callers: fail-open — never hide a row that can't be positively
 * attributed elsewhere.
 */
interface PersonSpFilterBannerProps {
  title: string;
  personGroups: PersonAccountGroup[];
  serviceProviders: ServiceProviderDto[];
  selectedPersonId: number | 'ALL';
  selectedSpRef: string;
  onPersonChange: (personId: number | 'ALL') => void;
  onSpChange: (spRef: string) => void;
}

export const guidEq = (a?: string | null, b?: string | null) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();

export const PersonSpFilterBanner: React.FC<PersonSpFilterBannerProps> = ({
  title, personGroups, serviceProviders, selectedPersonId, selectedSpRef, onPersonChange, onSpChange,
}) => {
  const [spMenuOpen, setSpMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const filtersRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (filtersRef.current && !filtersRef.current.contains(e.target as Node)) {
        setSpMenuOpen(false);
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const selectedGroup = selectedPersonId === 'ALL'
    ? null
    : personGroups.find((g) => g.personId === selectedPersonId) || null;
  const selectedSp = selectedSpRef === 'ALL'
    ? null
    : serviceProviders.find((sp) => guidEq(sp.spRef, selectedSpRef)) || null;

  return (
    <div
      className="text-white rounded shadow-sm px-4 py-3 d-flex justify-content-between align-items-center flex-wrap gap-2"
      style={{ background: 'linear-gradient(90deg, rgba(5, 47, 85, 1), rgba(7, 87, 155, 1)), radial-gradient(circle at 82% 20%, rgba(28, 166, 217, 1), transparent 34%)' }}
    >
      <h1 className="mb-0 fw-bold text-white" style={{ fontSize: 16 }}>{title}</h1>

      <div className="d-flex align-items-center gap-2" ref={filtersRef}>
        <div className="position-relative">
          <button
            type="button"
            onClick={() => { setSpMenuOpen((o) => !o); setAccountMenuOpen(false); }}
            className="btn btn-sm text-white fw-semibold"
            style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.25)', fontSize: 11 }}
          >
            <span style={{ opacity: 0.7 }}>Service Provider:&nbsp;</span>
            {selectedSp?.spName || 'All'}
            <i className="bi bi-chevron-down ms-2" style={{ fontSize: 9 }}></i>
          </button>
          {spMenuOpen && (
            <div className="position-absolute end-0 mt-1 bg-white rounded shadow border overflow-hidden" style={{ width: 240, zIndex: 1050 }}>
              <button
                type="button"
                onClick={() => { onSpChange('ALL'); setSpMenuOpen(false); }}
                className={`w-100 text-start border-0 bg-transparent px-3 py-2 ${selectedSpRef === 'ALL' ? 'fw-bold' : ''}`}
                style={{ fontSize: 11, color: selectedSpRef === 'ALL' ? '#b0842b' : '#334155', cursor: 'pointer' }}
              >
                All service providers
              </button>
              {serviceProviders.filter((sp) => sp.spRef).map((sp) => (
                <button
                  key={sp.spRef!}
                  type="button"
                  onClick={() => { onSpChange(sp.spRef!); setSpMenuOpen(false); }}
                  className={`w-100 text-start border-0 bg-transparent px-3 py-2 ${guidEq(sp.spRef, selectedSpRef) ? 'fw-bold' : ''}`}
                  style={{ fontSize: 11, color: guidEq(sp.spRef, selectedSpRef) ? '#b0842b' : '#334155', cursor: 'pointer' }}
                >
                  {sp.spName || 'Service Provider'}
                </button>
              ))}
              {serviceProviders.length === 0 && (
                <div className="px-3 py-2 text-muted" style={{ fontSize: 11 }}>No service providers yet</div>
              )}
            </div>
          )}
        </div>

        <div className="position-relative">
          <button
            type="button"
            onClick={() => { setAccountMenuOpen((o) => !o); setSpMenuOpen(false); }}
            className="btn btn-sm text-white fw-semibold"
            style={{ background: '#B0842B', border: '1px solid #B0842B', fontSize: 11 }}
          >
            <span style={{ opacity: 0.7 }}>Accounts:&nbsp;</span>
            {selectedGroup?.displayName || 'All'}
            <i className="bi bi-chevron-down ms-2" style={{ fontSize: 9 }}></i>
          </button>
          {accountMenuOpen && (
            <div className="position-absolute end-0 mt-1 bg-white rounded shadow border overflow-hidden" style={{ width: 240, zIndex: 1050 }}>
              <button
                type="button"
                onClick={() => { onPersonChange('ALL'); setAccountMenuOpen(false); }}
                className={`w-100 text-start border-0 bg-transparent px-3 py-2 ${selectedPersonId === 'ALL' ? 'fw-bold' : ''}`}
                style={{ fontSize: 11, color: selectedPersonId === 'ALL' ? '#b0842b' : '#334155', cursor: 'pointer' }}
              >
                All accounts
              </button>
              {personGroups.filter((g) => g.personId != null).map((g) => (
                <button
                  key={g.personId!}
                  type="button"
                  onClick={() => { onPersonChange(g.personId!); setAccountMenuOpen(false); }}
                  className={`w-100 text-start border-0 bg-transparent px-3 py-2 ${selectedPersonId === g.personId ? 'fw-bold' : ''}`}
                  style={{ fontSize: 11, color: selectedPersonId === g.personId ? '#b0842b' : '#334155', cursor: 'pointer' }}
                >
                  {g.displayName}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PersonSpFilterBanner;
